import React, { useMemo } from "react";
import styles from "./index.module.scss";
import Space from "components/Space";
import ListStatus from "../ListStatus";
import { Button, Divider, Icon, message, Modal } from "antd";
import {
    deleteDiscuss,
    DiscussListItem,
    listItemToFormData,
    startDiscuss,
    stopDiscuss
} from "service/discuss";
import { errorMessage, isTrue } from "service/service";
import useModalHandler from "utils/v3/useModalHandler";
import TopicEditor from "../../../../../AskTeacher/Components/TopicEditor";
import { pick } from "underscore";
import { Link, useRouteMatch } from "react-router-dom";
import TimeLimit from "./Components/TimeLimit";
import WordModal from "../WordModal";
import moment from "moment";
import { sleep } from "../../../../../../utils/common";

interface ListItemProps {
    data: DiscussListItem;
    onChange(): void;
}

export default function ListItem({ data, onChange }: ListItemProps) {
    const { path } = useRouteMatch();
    const topicModal = useModalHandler();

    function onDelete() {
        Modal.confirm({
            content: "确定要删除这个讨论吗? 删除后不可恢复!",
            onOk: async () => {
                try {
                    const { uuid, courseSeq, courseNo, batchNo } = data;
                    await deleteDiscuss({ uuid, courseSeq, courseNo, batchNo });
                    message.success("删除成功!");
                    onChange();
                } catch (e) {
                    message.error(errorMessage(e));
                }
            }
        });
    }

    function onEditOk() {
        topicModal.close();
        onChange();
    }

    function onStart() {
        Modal.confirm({
            content: "即将开始讨论, 请确认!",
            onOk: async () => {
                try {
                    const args = pick(data, [
                        "uuid",
                        "batchNo",
                        "courseNo",
                        "courseSeq"
                    ]);
                    await startDiscuss(args);
                    onChange();
                    message.success("讨论已经开始!");
                } catch (e) {
                    message.error(errorMessage(e));
                }
            }
        });
    }

    function onStop() {
        Modal.confirm({
            content: "确定要结束讨论吗?",
            onOk: async () => {
                try {
                    const args = pick(data, [
                        "uuid",
                        "batchNo",
                        "courseNo",
                        "courseSeq"
                    ]);
                    await stopDiscuss(args);
                    await sleep(4000);
                    onChange();
                    message.success("讨论已经结束!");
                } catch (e) {
                    message.error(errorMessage(e));
                }
            }
        });
    }

    const wordModal = useModalHandler({
        component: WordModal
    });

    function onWordOpen() {
        wordModal.open({
            uuid: data.uuid,
            onCancel: wordModal.close
        });
    }

    const isDead = useMemo(() => {
        return isTrue(data.isStart) && moment(data.deadline).isBefore();
    }, [data.deadline, data.isStart]);

    console.log(isDead);

    return (
        <Space className={styles.root}>
            <Space className={styles.main} direction="vertical">
                <Space size={20}>
                    <h1 className={styles.title}>
                        <Link to={`${path}/${data.uuid}`}>
                            {data.topicName}
                        </Link>
                    </h1>
                    <ListStatus
                        deadline={data.deadline}
                        isStart={data.isStart}
                    />

                    <Space.Spacer />

                    <Space split={<Divider type="vertical" />} size={0}>
                        <Button type="link" onClick={onWordOpen}>
                            词云展示
                        </Button>

                        {!isTrue(data.isStart) && (
                            <Button type="link" onClick={onStart}>
                                开始讨论
                            </Button>
                        )}

                        {isTrue(data.isStart) && !isDead && (
                            <Button type="link" onClick={onStop}>
                                结束讨论
                            </Button>
                        )}

                        <Button type="link" onClick={topicModal.open}>
                            修改设置
                        </Button>

                        <Button type="link" onClick={onDelete}>
                            删除讨论
                        </Button>
                    </Space>
                </Space>

                <Link
                    to={`${path}/${data.uuid}`}
                    className={styles.summary}
                    dangerouslySetInnerHTML={{ __html: data.topicContent }}
                />

                <Space className={styles.footer}>
                    <Space
                        className={styles.info}
                        split={<Divider type="vertical" />}
                        size={0}>
                        <TimeLimit data={data} />

                        <span>
                            {isTrue(data.canMutualReplay) ? "允许" : "禁止"}
                            相互回复
                        </span>

                        <span>{isTrue(data.showLike) ? "" : "不"}展示赞同</span>

                        <span>
                            {isTrue(data.showDislike) ? "" : "不"}展示不赞同
                        </span>

                        <span>
                            {isTrue(data.showStudentName) ? "" : "不"}
                            展示学生姓名
                        </span>
                    </Space>

                    <Space.Spacer />

                    <Space className={styles.statics} size={15}>
                        <span>
                            <Icon type="solution" />
                            {data.statics?.partInCount || 0}
                            人参与
                        </span>
                        <span>
                            <Icon type="message" />
                            {data.statics?.replyCount || 0}条发言
                        </span>
                    </Space>
                </Space>
            </Space>

            {topicModal.render(
                <TopicEditor
                    initialValues={listItemToFormData(data)}
                    onOk={onEditOk}
                />
            )}

            {wordModal.render()}
        </Space>
    );
}
